/**
 * The docs' pages and their addresses. Each page is `/docs/<page>`, and
 * `/docs` opens the first. The ids are English and the same in every
 * language; the docs' content gives each page its title. The docs, the Home
 * page and Settings' About all link here, so the list lives outside the docs.
 */

/** Where the docs start: the sidebar's link, current on every page under it. */
export const DOCS_PATH = "/docs";

/**
 * The pages in the order a newcomer reads them: connect Claude, then what to
 * ask, the tools Claude uses for it, what the connector cannot do, what
 * happens to the data, and last the answers to questions and problems.
 */
export const DOC_PAGES = [
  "connect-claude",
  "what-you-can-ask",
  "tools",
  "limits",
  "privacy-and-data",
  "troubleshooting",
] as const;

export type DocPageId = (typeof DOC_PAGES)[number];

export function isDocPage(value: string): value is DocPageId {
  return (DOC_PAGES as readonly string[]).includes(value);
}

/** A page's address: `/docs/limits`. */
export function docsPath(page: DocPageId): string {
  return `${DOCS_PATH}/${page}`;
}

/** The page before and the page after, for the links at the bottom; null at either end. */
export function docNeighbours(page: DocPageId): {
  previous: DocPageId | null;
  next: DocPageId | null;
} {
  const index = DOC_PAGES.indexOf(page);
  return {
    previous: DOC_PAGES[index - 1] ?? null,
    next: DOC_PAGES[index + 1] ?? null,
  };
}

/**
 * The sections of the docs from when they were one long page, each with the
 * page that holds it now, so a bookmark or an old link to `/docs#<section>`
 * still lands on it. Every former section is a page of its own, or the start
 * of one: "what-it-can-do" begins the page on what to ask, and its list of
 * tools is the page after it.
 */
export const FORMER_SECTIONS = {
  "connect-claude": "connect-claude",
  "what-it-can-do": "what-you-can-ask",
  "what-it-cannot-do": "limits",
  "privacy-and-data": "privacy-and-data",
  troubleshooting: "troubleshooting",
} as const satisfies Record<string, DocPageId>;

/**
 * Where `/docs` with this hash leads: the page that holds a former section
 * ("#what-it-cannot-do" → `/docs/limits`), else the first page. A malformed
 * hash, or one that names no former section, also opens the first page.
 */
export function docsStartPath(hash: string): string {
  let id: string;
  try {
    id = decodeURIComponent(hash.replace(/^#/, ""));
  } catch {
    id = "";
  }
  const page = Object.hasOwn(FORMER_SECTIONS, id)
    ? FORMER_SECTIONS[id as keyof typeof FORMER_SECTIONS]
    : DOC_PAGES[0];
  return docsPath(page);
}
